/**
 * Studio's platform: a health line and four headline numbers, then tabs for the
 * Lambda MicroVM lane (fleet counts and every VM of the workspace image with its
 * workspace and snapshots), failures (recent claim failures and how long opening
 * a workspace takes), capacity (lane and tenant capacity, workspace minutes, the
 * workspace image), a live feed of spawns, suspends and terminations, Workflows
 * (WorkflowsTab: runs, failures, capacity, usage), and the default coding agent
 * (CodingAgentSection). Fed by the ops snapshot, polled
 * every few seconds; the worker observes AWS, the API never calls it.
 */
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ConnectionStatusChip } from '@/components/ops/ConnectionStatusChip';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { StatTile } from '@/components/shell/StatTile';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  type LatencyStats,
  type MicrovmFleet,
  type MicrovmFleetEvent,
  type MicrovmFleetVm,
  type OpsSnapshot,
  type WorkspaceMinutes,
  useOpsSnapshot,
} from '@/hooks/useOpsSnapshot';
import { formatUsd } from '@/lib/format';
import {
  MICROVM_STATE_LABELS,
  busiestTenants,
  currentImageVersion,
  filterVms,
  formatAge,
  formatMinutes,
  formatSpan,
  lifecycleLabel,
  lifecycleTone,
  lifetimeLeftMs,
  microvmPreviewLatency,
  microvmStateTone,
  previewPathLabel,
  providerLabel,
  recentFailures,
  runPathFor,
  sessionPathFor,
  snapshotLabel,
  type MicrovmFilter,
} from '@/lib/microvm';
import {
  PLATFORM_HEALTH_LABELS,
  failuresInLastDay,
  imageLag,
  microvmFilterFrom,
  platformHealth,
  platformTabFrom,
  type PlatformHealth,
  type PlatformHealthLevel,
  type PlatformHealthReason,
  type PlatformTab,
} from '@/lib/platformHealth';
import { formatBytes, formatClockTime, formatDurationMs, humanizeKey, type StatusTone } from '@/lib/runTelemetry';
import { cn } from '@/lib/utils';

import { CodingAgentSection } from './CodingAgentSection';
import { WorkflowsTab } from './WorkflowsTab';

const MINUTES_WINDOWS: Array<{ id: 'today' | 'last7Days'; label: string }> = [
  { id: 'today', label: 'Today (UTC)' },
  { id: 'last7Days', label: 'Last 7 days' },
];

const FILTERS: Array<{ id: MicrovmFilter; label: string }> = [
  { id: 'live', label: 'Live' },
  { id: 'orphans', label: 'Orphans' },
  { id: 'outdated', label: 'Old image' },
  { id: 'all', label: 'All listed' },
];

function Stat({ label, value, hint, tone }: { label: string; value: number | string; hint?: string; tone?: 'success' | 'warning' | 'danger' }) {
  return (
    <div className="rounded-xl border border-border bg-card/40 px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-1 font-mono text-2xl font-semibold tabular-nums',
          tone === 'success' && 'text-success',
          tone === 'warning' && 'text-warning',
          tone === 'danger' && 'text-destructive',
        )}
      >
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function FleetStats({ fleet }: { fleet: MicrovmFleet }) {
  const suspended = fleet.counts.SUSPENDED + fleet.counts.SUSPENDING;
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <Stat label="Running" value={fleet.counts.RUNNING} tone={fleet.counts.RUNNING ? 'success' : undefined} hint={`${fleet.live} holding quota`} />
      <Stat label="Starting" value={fleet.counts.PENDING} tone={fleet.counts.PENDING ? 'warning' : undefined} />
      <Stat label="Suspended" value={suspended} hint="Frozen with a snapshot" />
      <Stat label="Terminating" value={fleet.counts.TERMINATING} />
      <Stat label="Orphans" value={fleet.orphans} tone={fleet.orphans ? 'danger' : undefined} hint="No workspace owns them" />
      <Stat
        label="Spawned last hour"
        value={fleet.lastHour.spawned ?? 0}
        hint={`${fleet.lastHour.suspended ?? 0} suspended · ${(fleet.lastHour.terminated ?? 0) + (fleet.lastHour.orphan_terminated ?? 0)} ended`}
      />
    </div>
  );
}

function seconds(ms: number | null): string {
  return ms === null ? '—' : `${(ms / 1000).toFixed(1)}s`;
}

function LatencyRow({ label, stats }: { label: string; stats: LatencyStats }) {
  return (
    <div className="grid grid-cols-[1fr_repeat(3,4.5rem)] items-baseline gap-2 py-1.5 text-sm">
      <span className="min-w-0 truncate">
        {label}
        <span className="ml-1.5 text-xs text-muted-foreground">
          {stats.samples} sample{stats.samples === 1 ? '' : 's'}
        </span>
      </span>
      <span className="text-right font-mono tabular-nums">{seconds(stats.p50Ms)}</span>
      <span className={cn('text-right font-mono tabular-nums', stats.p95Ms !== null && stats.p95Ms > 10_000 && 'text-warning')}>
        {seconds(stats.p95Ms)}
      </span>
      <span className="text-right font-mono tabular-nums text-muted-foreground">{seconds(stats.maxMs)}</span>
    </div>
  );
}

function LatencySection({ sandbox }: { sandbox: OpsSnapshot['sandbox'] }) {
  const paths = microvmPreviewLatency(sandbox);
  const signals = sandbox.previewSignals;
  return (
    <AdminSection
      title="Open to preview"
      description="From a workspace claim to the preview answering (Vite document and App Service), for MicroVM workspaces over the last 24 hours. Successful opens only."
    >
      {paths.length === 0 ? (
        <p className="text-sm text-muted-foreground">No MicroVM opens in the window.</p>
      ) : (
        <div className="divide-y divide-border">
          <div className="grid grid-cols-[1fr_repeat(3,4.5rem)] gap-2 pb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <span>Path</span>
            <span className="text-right">p50</span>
            <span className="text-right">p95</span>
            <span className="text-right">max</span>
          </div>
          {paths.map(([path, stats]) => (
            <LatencyRow key={path} label={previewPathLabel(path)} stats={stats} />
          ))}
          {signals && (signals.vite.samples > 0 || signals.appService.samples > 0) ? (
            <>
              <LatencyRow label="Vite first answer" stats={signals.vite} />
              <LatencyRow label="App Service first answer" stats={signals.appService} />
            </>
          ) : null}
        </div>
      )}
    </AdminSection>
  );
}

function FailuresSection({ sandbox, now }: { sandbox: OpsSnapshot['sandbox']; now: number }) {
  const failures = recentFailures(sandbox);
  return (
    <AdminSection title="Recent failures" description="Workspace claims that failed in the last 24 hours, newest first." bodyClassName="p-0">
      {failures.length === 0 ? (
        <p className="flex items-center gap-2 px-5 py-6 text-sm text-success">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          No failures in the window.
        </p>
      ) : (
        <ol className="max-h-80 divide-y divide-border overflow-y-auto">
          {failures.map((failure, index) => (
            <li key={`${failure.scopeHash}:${failure.generation}:${index}`} className="flex items-start justify-between gap-3 px-5 py-2.5">
              <div className="min-w-0">
                <code className="text-xs text-destructive">{failure.code}</code>
                <div className="truncate font-mono text-[11px] text-muted-foreground">
                  {failure.scopeHash}:{failure.generation}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <StatusPill tone={failure.provider === 'lambda-microvm' ? 'neutral' : 'warning'}>{providerLabel(failure.provider)}</StatusPill>
                <span className="w-12 text-right font-mono text-[11px] tabular-nums text-muted-foreground">{formatAge(failure.at, now)}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </AdminSection>
  );
}

function CapacitySection({ sandbox }: { sandbox: OpsSnapshot['sandbox'] }) {
  const lanes = Object.entries(sandbox.lanes ?? {}).sort((a, b) => b[1].active - a[1].active);
  const tenants = busiestTenants(sandbox);
  return (
    <AdminSection title="Capacity" description="Active workspaces per lane and per organization, against each organization's limit.">
      <div className="space-y-4">
        {lanes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {lanes.map(([lane, row]) => (
              <span key={lane} className="rounded-lg border border-border px-2.5 py-1 text-xs">
                <span className="text-muted-foreground">{humanizeKey(lane)}</span>
                <span className="ml-2 font-mono tabular-nums">{row.active}</span>
              </span>
            ))}
          </div>
        ) : null}
        {tenants.length === 0 ? (
          <p className="text-sm text-muted-foreground">No organization has an active workspace.</p>
        ) : (
          <ul className="divide-y divide-border text-sm">
            {tenants.map((tenant) => (
              <li key={tenant.tenantId} className="flex items-baseline justify-between gap-3 py-1.5">
                <Link to={`/organizations/${encodeURIComponent(tenant.tenantId)}`} className="min-w-0 truncate text-primary hover:underline">
                  {tenant.organizationName}
                </Link>
                <span className={cn('font-mono tabular-nums', tenant.atLimit && 'text-destructive')}>
                  {tenant.active}
                  {tenant.limit !== null ? <span className="text-muted-foreground"> / {tenant.limit}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
        {sandbox.tenants.length > tenants.length ? (
          <p className="text-xs text-muted-foreground">+{sandbox.tenants.length - tenants.length} more organizations</p>
        ) : null}
      </div>
    </AdminSection>
  );
}

function WorkspaceMinutesSection({ minutes }: { minutes: WorkspaceMinutes }) {
  const [windowId, setWindowId] = useState<'today' | 'last7Days'>('today');
  const totals = minutes[windowId];
  const priced = minutes.pricePerHourUsd !== undefined;
  return (
    <AdminSection
      title="Workspace minutes"
      description={`How long MicroVM workspaces ran${windowId === 'today' ? ' since midnight UTC' : ' over the last 7 days'}, from the lifecycle log. Idle is the stretch from the last person input to an idle suspend; the rest is active.${minutes.truncated ? ' More events than one read takes: the oldest days are undercounted.' : ''}`}
      actions={
        <div className="flex gap-1">
          {MINUTES_WINDOWS.map((option) => (
            <Button key={option.id} type="button" size="sm" variant={windowId === option.id ? 'secondary' : 'ghost'} onClick={() => setWindowId(option.id)}>
              {option.label}
            </Button>
          ))}
        </div>
      }
    >
      <div className="space-y-4">
        <div className={cn('grid gap-3 sm:grid-cols-2', priced ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
          <Stat label="Running" value={formatMinutes(totals.runningMs)} hint={`${totals.workspaces} workspace${totals.workspaces === 1 ? '' : 's'} · ${formatSpan(totals.runningMs)}`} />
          <Stat label="Active" value={formatMinutes(totals.activeMs)} tone={totals.activeMs ? 'success' : undefined} hint="Someone was working" />
          <Stat label="Idle before suspend" value={formatMinutes(totals.idleMs)} tone={totals.idleMs ? 'warning' : undefined} hint="Last input to idle suspend" />
          <Stat label="Service VMs" value={formatMinutes(totals.services.runningMs)} hint={`${totals.services.vms} child VM${totals.services.vms === 1 ? '' : 's'}, not counted above`} />
          {priced ? (
            <Stat
              label="Estimated cost"
              value={formatUsd((totals.costUsd ?? 0) + (totals.services.costUsd ?? 0))}
              hint={`${formatUsd(minutes.pricePerHourUsd)} per VM-hour, service VMs included`}
            />
          ) : null}
        </div>
        {totals.byOrganization.length === 0 ? (
          <p className="text-sm text-muted-foreground">No workspace ran in this window.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3 font-medium">Organization</th>
                  <th className="px-3 py-2 text-right font-medium">Workspaces</th>
                  <th className="px-3 py-2 text-right font-medium">Running</th>
                  <th className="px-3 py-2 text-right font-medium">Active</th>
                  <th className="px-3 py-2 text-right font-medium">Idle</th>
                  {priced ? <th className="py-2 pl-3 text-right font-medium">Cost</th> : null}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {totals.byOrganization.map((org) => (
                  <tr key={org.organizationId ?? 'unattributed'}>
                    <td className="max-w-[16rem] truncate py-1.5 pr-3">
                      {org.organizationId ? (
                        <Link to={`/organizations/${encodeURIComponent(org.organizationId)}`} className="text-primary hover:underline">
                          {org.organizationName ?? org.organizationId}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">Unattributed</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{org.workspaces}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatMinutes(org.runningMs)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatMinutes(org.activeMs)}</td>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-muted-foreground">{formatMinutes(org.idleMs)}</td>
                    {priced ? <td className="py-1.5 pl-3 text-right font-mono tabular-nums">{formatUsd(org.costUsd)}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminSection>
  );
}

function ImageSection({ fleet }: { fleet: MicrovmFleet }) {
  const launching = fleet.image.pinnedVersion ?? fleet.image.activeVersion;
  const phases = Object.entries(fleet.rowPhases).sort((a, b) => b[1] - a[1]);
  return (
    <AdminSection
      title="Workspace image"
      description="Every VM restores this image's prewarmed snapshot. New VMs launch from the pinned version, or the latest ACTIVE one."
    >
      <dl className="grid gap-4 text-sm sm:grid-cols-3">
        <div className="min-w-0">
          <dt className="text-xs text-muted-foreground">Image</dt>
          <dd className="truncate font-mono text-xs" title={fleet.image.arn}>
            {fleet.image.arn?.split(':').pop() ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">New VMs launch from</dt>
          <dd className="font-mono">
            {launching ? `v${launching}` : '—'}
            {fleet.image.pinnedVersion ? <span className="ml-1.5 text-xs text-muted-foreground">(pinned)</span> : null}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Versions in use</dt>
          <dd className="flex flex-wrap gap-1.5 pt-0.5">
            {fleet.byImageVersion.length === 0 ? <span className="text-muted-foreground">None</span> : null}
            {fleet.byImageVersion.map((row) => (
              <StatusPill key={row.version} tone={row.active ? 'success' : 'warning'}>
                v{row.version} · {row.count}
              </StatusPill>
            ))}
          </dd>
        </div>
      </dl>
      {phases.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <span>Workspace rows on the MicroVM lane, live:</span>
          {phases.map(([phase, count]) => (
            <span key={phase} className="rounded border border-border px-1.5 py-0.5 font-mono">
              {phase} {count}
            </span>
          ))}
        </div>
      ) : null}
    </AdminSection>
  );
}

function WorkspaceCell({ vm }: { vm: MicrovmFleetVm }) {
  if (!vm.owned) return <span className="text-xs text-destructive">No owner</span>;
  const path = sessionPathFor(vm);
  const label = vm.organizationName ?? vm.organizationId ?? 'Workspace';
  return (
    <div className="min-w-0">
      {path ? (
        <Link to={path} className="text-sm text-primary hover:underline">
          {label}
        </Link>
      ) : (
        <span className="text-sm">{label}</span>
      )}
      <div className="truncate font-mono text-[11px] text-muted-foreground" title={vm.sessionId}>
        {vm.sessionId ? vm.sessionId.slice(0, 8) : '—'}
        {vm.phase ? ` · ${vm.phase}` : ''}
        {vm.generation ? ` · gen ${vm.generation}` : ''}
      </div>
    </div>
  );
}

function VmTable({
  fleet,
  now,
  filter,
  onFilterChange,
}: {
  fleet: MicrovmFleet;
  now: number;
  filter: MicrovmFilter;
  onFilterChange: (filter: MicrovmFilter) => void;
}) {
  const vms = filterVms(fleet.vms, filter, currentImageVersion(fleet));
  return (
    <AdminSection
      title="MicroVMs"
      description={`What ListMicrovms reported ${fleet.observedAt ? `${formatAge(fleet.observedAt, now)} ago` : 'last'}, joined with the workspace that owns each VM.${fleet.truncated ? ' Showing the first 300.' : ''}`}
      actions={
        <div className="flex gap-1">
          {FILTERS.map((option) => (
            <Button key={option.id} type="button" size="sm" variant={filter === option.id ? 'secondary' : 'ghost'} onClick={() => onFilterChange(option.id)}>
              {option.label}
            </Button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      {vms.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground">No MicroVMs match.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-5 py-2 font-medium">MicroVM</th>
                <th className="px-3 py-2 font-medium">State</th>
                <th className="px-3 py-2 font-medium">Workspace</th>
                <th className="px-3 py-2 font-medium">Image</th>
                <th className="px-3 py-2 font-medium">Uptime</th>
                <th className="px-3 py-2 font-medium">Lifetime left</th>
                <th className="px-3 py-2 font-medium">Suspended</th>
                <th className="px-3 py-2 font-medium">Last snapshot</th>
                <th className="px-5 py-2 font-medium">Last claim</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {vms.map((vm) => {
                const left = lifetimeLeftMs(vm, now);
                return (
                  <tr key={vm.microvmId} className="align-top">
                    <td className="px-5 py-2.5">
                      <span className="font-mono text-xs" title={vm.microvmId}>
                        {vm.microvmId}
                      </span>
                      {vm.lastError ? (
                        <div className="mt-1 flex items-center gap-1 text-[11px] text-destructive" title={vm.lastError.message}>
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          {vm.lastError.code}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusPill tone={microvmStateTone(vm.state)}>{MICROVM_STATE_LABELS[vm.state]}</StatusPill>
                      {vm.stateReason ? <div className="mt-1 text-[11px] text-muted-foreground">{vm.stateReason}</div> : null}
                    </td>
                    <td className="max-w-[16rem] px-3 py-2.5">
                      <WorkspaceCell vm={vm} />
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{vm.imageVersion ? `v${vm.imageVersion}` : '—'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs tabular-nums">{formatAge(vm.startedAt, now)}</td>
                    <td className={cn('px-3 py-2.5 font-mono text-xs tabular-nums', left !== undefined && left < 30 * 60_000 && 'text-warning')}>
                      {left === undefined ? '—' : formatSpan(left)}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs tabular-nums">{vm.suspendedAt ? `${formatAge(vm.suspendedAt, now)} ago` : '—'}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {vm.lastSnapshot ? (
                        <div title={vm.lastSnapshot.uri}>
                          <span className="font-mono">{formatBytes(vm.lastSnapshot.bytes)}</span>
                          <span className="text-muted-foreground"> · {formatAge(vm.lastSnapshot.at, now)} ago</span>
                          <div className="font-mono text-[11px] text-muted-foreground">{snapshotLabel(vm.lastSnapshot.uri)}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">None yet</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-xs">
                      {vm.lastRebound ? (
                        <>
                          <span className="font-mono tabular-nums">{formatDurationMs(vm.lastRebound.claimWaitMs)}</span>
                          <span className="text-muted-foreground"> · {humanizeKey(vm.lastRebound.path)}</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  );
}

function LifecycleFeed({ events }: { events: MicrovmFleetEvent[] }) {
  return (
    <AdminSection title="Lifecycle" description="Spawns, wake-ups, suspends and terminations as the worker made or saw them, newest first. Kept 14 days." bodyClassName="p-0">
      {events.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted-foreground">No lifecycle events recorded yet.</p>
      ) : (
        <ol className="divide-y divide-border">
          {events.map((event, index) => {
            const sessionPath = sessionPathFor(event);
            const runPath = runPathFor(event);
            return (
              <li key={`${event.at}:${event.microvmId}:${event.type}:${index}`} className="grid grid-cols-[6.5rem_9rem_1fr] items-start gap-3 px-5 py-2 text-sm">
                <span className="pt-0.5 font-mono text-xs tabular-nums text-muted-foreground">{formatClockTime(event.at)}</span>
                <span>
                  <StatusPill tone={lifecycleTone(event.type)}>{lifecycleLabel(event.type)}</StatusPill>
                </span>
                <div className="min-w-0 space-y-0.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 text-xs">
                    <span className="font-mono">{event.microvmId}</span>
                    {event.durationMs !== undefined ? <span className="tabular-nums text-muted-foreground">{formatDurationMs(event.durationMs)}</span> : null}
                    {event.idleMs !== undefined ? <span className="tabular-nums text-muted-foreground">idle {formatSpan(event.idleMs)}</span> : null}
                    {event.imageVersion ? <span className="text-muted-foreground">v{event.imageVersion}</span> : null}
                    {event.reason ? <span className="text-muted-foreground">{humanizeKey(event.reason)}</span> : null}
                  </div>
                  <div className="flex flex-wrap gap-x-3 text-xs">
                    {sessionPath ? (
                      <Link to={sessionPath} className="text-primary hover:underline">
                        {event.organizationName ?? 'Session'}
                      </Link>
                    ) : null}
                    {runPath ? (
                      <Link to={runPath} className="text-primary hover:underline">
                        Run {event.runId!.slice(0, 8)}
                      </Link>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </AdminSection>
  );
}

const HEALTH_TONES: Record<PlatformHealthLevel, StatusTone> = {
  healthy: 'success',
  warning: 'warning',
  critical: 'danger',
  unknown: 'neutral',
};

const TABS: Array<{ id: PlatformTab; label: string }> = [
  { id: 'microvms', label: 'MicroVMs' },
  { id: 'failures', label: 'Failures' },
  { id: 'capacity', label: 'Capacity' },
  { id: 'lifecycle', label: 'Lifecycle' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'agent', label: 'Coding agent' },
];

function HealthLine({
  health,
  onOpen,
  onReconnect,
}: {
  health: PlatformHealth;
  onOpen: (reason: PlatformHealthReason) => void;
  onReconnect: () => void;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusPill tone={HEALTH_TONES[health.level]}>{PLATFORM_HEALTH_LABELS[health.level]}</StatusPill>
        <p className="min-w-0 flex-1 text-sm">{health.sentence}</p>
      </div>
      {health.reasons.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {health.reasons.map((reason) =>
            reason.action ? (
              <Button key={reason.id} type="button" size="sm" variant="outline" onClick={() => onOpen(reason)}>
                {reason.action.label}
              </Button>
            ) : (
              <Button key={reason.id} type="button" size="sm" variant="outline" onClick={onReconnect}>
                Reconnect
              </Button>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

function imageDetail(fleet: MicrovmFleet | null | undefined): string {
  if (!fleet) return 'The MicroVM lane is not reporting';
  const lag = imageLag(fleet);
  if (lag.outdated === 0) return 'Every running MicroVM is on it';
  const behind = lag.versionsBehind ? `, oldest ${lag.versionsBehind} version${lag.versionsBehind === 1 ? '' : 's'} behind` : '';
  return `${lag.outdated} MicroVM${lag.outdated === 1 ? '' : 's'} on an older one${behind}`;
}

function HeadlineNumbers({ snapshot, now }: { snapshot: OpsSnapshot; now: number }) {
  const fleet = snapshot.microvm;
  const busiest = microvmPreviewLatency(snapshot.sandbox)[0];
  const failures = failuresInLastDay(snapshot.sandbox, now);
  const current = fleet ? currentImageVersion(fleet) : undefined;
  const outdated = fleet ? imageLag(fleet).outdated : 0;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatTile
        label="Running"
        value={fleet ? fleet.counts.RUNNING : '—'}
        detail={fleet ? `${fleet.live} holding quota · ${fleet.orphans} with no owner` : 'The MicroVM lane is not reporting'}
        to="?tab=microvms"
      />
      <StatTile
        label="Open to preview, p95"
        value={busiest ? seconds(busiest[1].p95Ms) : '—'}
        detail={busiest ? `${previewPathLabel(busiest[0])} · ${busiest[1].samples} open${busiest[1].samples === 1 ? '' : 's'} in 24h` : 'No opens in the last 24 hours'}
        to="?tab=failures"
      />
      <StatTile
        label="Failures in 24h"
        value={failures}
        detail={failures ? 'Workspaces that failed to open' : 'Every workspace opened'}
        to="?tab=failures"
      />
      <StatTile
        label="Workspace image"
        value={current ? `v${current}` : '—'}
        detail={imageDetail(fleet)}
        to={outdated ? '?tab=microvms&vms=outdated' : '?tab=capacity'}
      />
    </div>
  );
}

function NoFleet() {
  return (
    <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
      The MicroVM lane is not configured on this API, or the worker has not observed the fleet yet.
    </p>
  );
}

export function PlatformPage() {
  const { snapshot, status, lastUpdatedAt, error, reconnect } = useOpsSnapshot();
  const [params, setParams] = useSearchParams();
  const tab = platformTabFrom(params.get('tab'));
  const vmFilter = microvmFilterFrom(params.get('vms'));
  const fleet = snapshot?.microvm;
  const now = snapshot ? Date.parse(snapshot.generatedAt) : Date.now();
  const health = platformHealth({ snapshot, status });

  const updateParams = (next: { tab?: PlatformTab; vms?: MicrovmFilter }) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next.tab) updated.set('tab', next.tab);
        if (next.vms === 'live') updated.delete('vms');
        else if (next.vms) updated.set('vms', next.vms);
        return updated;
      },
      { replace: true },
    );

  let placeholder: ReactNode = null;
  if (status === 'offline' && error === 'Access denied') {
    placeholder = <p className="text-sm text-muted-foreground">Platform monitoring is limited to platform admins.</p>;
  } else if (!snapshot && error && status !== 'connecting') {
    placeholder = (
      <LoadError
        message={`Couldn't load platform status: ${error}`}
        onRetry={reconnect}
        className="rounded-xl border border-border bg-card"
      />
    );
  } else if (!snapshot) {
    placeholder = (
      <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading platform status
      </div>
    );
  }

  const lane = (render: (snapshot: OpsSnapshot) => ReactNode) => placeholder ?? (snapshot ? render(snapshot) : null);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Platform"
        description="Whether Studio's workspaces and Workflows are healthy right now, and the details behind it: every MicroVM, failed opens, capacity, the lifecycle feed, Workflows runs and capacity, and the coding agent new builds run on."
        actions={<ConnectionStatusChip status={status} lastUpdatedAt={lastUpdatedAt} onReconnect={reconnect} />}
      />
      {snapshot && !placeholder ? (
        <div className="space-y-4">
          <HealthLine
            health={health}
            onOpen={(reason) => reason.action && updateParams({ tab: reason.action.tab, vms: reason.action.filter })}
            onReconnect={reconnect}
          />
          <HeadlineNumbers snapshot={snapshot} now={now} />
        </div>
      ) : null}
      <Tabs value={tab} onValueChange={(value) => updateParams({ tab: platformTabFrom(value) })} className="mt-6">
        <div className="overflow-x-auto">
          <TabsList>
            {TABS.map((option) => (
              <TabsTrigger key={option.id} value={option.id}>
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        <TabsContent value="microvms" className="space-y-6">
          {lane(() =>
            fleet ? (
              <>
                <FleetStats fleet={fleet} />
                <VmTable fleet={fleet} now={now} filter={vmFilter} onFilterChange={(vms) => updateParams({ vms })} />
              </>
            ) : (
              <NoFleet />
            ),
          )}
        </TabsContent>
        <TabsContent value="failures" className="space-y-6">
          {lane((current) => (
            <>
              <FailuresSection sandbox={current.sandbox} now={now} />
              <LatencySection sandbox={current.sandbox} />
            </>
          ))}
        </TabsContent>
        <TabsContent value="capacity" className="space-y-6">
          {lane((current) => (
            <>
              <CapacitySection sandbox={current.sandbox} />
              {current.workspaceMinutes ? <WorkspaceMinutesSection minutes={current.workspaceMinutes} /> : null}
              {fleet ? <ImageSection fleet={fleet} /> : null}
            </>
          ))}
        </TabsContent>
        <TabsContent value="lifecycle">{lane(() => (fleet ? <LifecycleFeed events={fleet.recent} /> : <NoFleet />))}</TabsContent>
        <TabsContent value="workflows">{lane((current) => <WorkflowsTab workflows={current.workflows} now={now} />)}</TabsContent>
        <TabsContent value="agent">
          <CodingAgentSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default PlatformPage;
